import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useProject } from "./useProject";
import { toast } from "sonner";

export interface TemplateImage {
  id: number;
  template_id: number;
  project_id: number;
  name: string;
  description?: string;
  slide_number: number;
  position_x: number;
  position_y: number;
  width: number;
  height: number;
  image_type: "url" | "upload" | "client_logo" | "qr_code";
  fit_mode: "fixed" | "fit" | "fill";
  image_config: Record<string, any>;
  display_order: number;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface CreateTemplateImageInput {
  template_id: number;
  name: string;
  description?: string;
  slide_number: number;
  position_x: number;
  position_y: number;
  width: number;
  height: number;
  image_type: "url" | "upload" | "client_logo" | "qr_code";
  fit_mode?: "fixed" | "fit" | "fill";
  image_config: Record<string, any>;
  display_order?: number;
  is_active?: boolean;
}

export interface UpdateTemplateImageInput {
  id: number;
  name?: string;
  description?: string;
  slide_number?: number;
  position_x?: number;
  position_y?: number;
  width?: number;
  height?: number;
  image_type?: "url" | "upload" | "client_logo" | "qr_code";
  fit_mode?: "fixed" | "fit" | "fill";
  image_config?: Record<string, any>;
  display_order?: number;
  is_active?: boolean;
}

export function useTemplateImages(templateId: number | null) {
  const { projectClient } = useProject();
  const queryClient = useQueryClient();

  // Fetch images for a template
  const { data: images = [], isLoading, error } = useQuery({
    queryKey: ["template_images", templateId],
    queryFn: async () => {
      if (!templateId) return [];

      const { data, error } = await projectClient
        .from("template_images")
        .select("*")
        .eq("template_id", templateId)
        .eq("project_id", projectClient.getProjectId())
        .order("display_order");

      if (error) throw error;
      return data as TemplateImage[];
    },
    enabled: !!templateId,
  });

  // Create image mutation
  const createMutation = useMutation({
    mutationFn: async (input: CreateTemplateImageInput) => {
      const { data, error } = await projectClient
        .from("template_images")
        .insert({
          ...input,
          project_id: projectClient.getProjectId(),
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success("Imagen creada exitosamente");
      queryClient.invalidateQueries({ queryKey: ["template_images", templateId] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Error al crear imagen");
    },
  });

  // Update image mutation
  const updateMutation = useMutation({
    mutationFn: async (input: UpdateTemplateImageInput) => {
      const { id, ...updates } = input;

      const { data, error } = await projectClient
        .from("template_images")
        .update(updates)
        .eq("id", id)
        .eq("project_id", projectClient.getProjectId())
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success("Imagen actualizada exitosamente");
      queryClient.invalidateQueries({ queryKey: ["template_images", templateId] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Error al actualizar imagen");
    },
  });

  // Delete image mutation
  const deleteMutation = useMutation({
    mutationFn: async (imageId: number) => {
      const { error } = await projectClient
        .from("template_images")
        .delete()
        .eq("id", imageId)
        .eq("project_id", projectClient.getProjectId());

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Imagen eliminada exitosamente");
      queryClient.invalidateQueries({ queryKey: ["template_images", templateId] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Error al eliminar imagen");
    },
  });

  return {
    images,
    isLoading,
    error,
    createImage: createMutation.mutateAsync,
    updateImage: updateMutation.mutateAsync,
    deleteImage: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}
